import { ProCard } from '@ant-design/pro-components'
import { sync } from '@app/icare/client/sync'
import {
  type CareService,
  type CareServiceIdType,
  CareServiceSchema,
} from '@app/icare/entities/careService/schema/CareService'
import { entityRegistry } from '@app/icare/entities/registry'
import { EntityDrawerShell } from '@app/icare/form/EntityDrawerShell'
import { AddressFieldGroup } from '@app/icare/form/group/AddressFieldGroup'
import { useEntityDrawer } from '@app/icare/hooks/useEntityDrawer'
import { t } from '@lingui/core/macro'
import { SectionFlow } from '@pkg/form/index'
import type { SectionDef } from '@pkg/form/useSectionFlow'
import { zodDefaultInstance } from '@pkg/shared/form'
import { createTypedModal, type EditModalProps, type EditModalResult } from '@pkg/ui/modal'
import { Flex } from 'antd'

const sections = [
  { name: 'basic', title: t`Allgemein` },
  { name: 'misc', title: t`Sonstiges` },
] as const satisfies readonly SectionDef[]

type CareServiceEditDrawerProps =
  | (EditModalProps<CareService, (typeof sections)[number]['name'], CareServiceIdType> & {
      showGlobalImpactWarning?: boolean
    })
  | undefined

type CareServiceEditDrawerShow = (props?: CareServiceEditDrawerProps) => Promise<EditModalResult<CareService>>

export const CareServiceEditDrawer = createTypedModal<
  CareServiceEditDrawerProps,
  EditModalResult<CareService>,
  CareServiceEditDrawerShow
>(props => {
  const entityId = props?.id ?? props?.defaults?.id
  const schema = entityRegistry.careService.schema

  const drawer = useEntityDrawer({
    schema: CareServiceSchema,
    entityId,
    sections,
    section: props?.section,
    formDefaults: props?.defaults ? { ...zodDefaultInstance(schema), ...props.defaults } : undefined,
    onPersist: async value => {
      await sync.mutate(value)
    },
  })

  const { form } = drawer

  return (
    <EntityDrawerShell drawer={drawer} showWarnings={props?.showGlobalImpactWarning}>
      <SectionFlow.Sections
        items={[
          {
            name: 'basic',
            title: t`Allgemein`,
            children: (
              <ProCard>
                <Flex vertical gap="small">
                  <form.AppField
                    name="name"
                    children={field => (
                      <field.Text label="Name" className="col-span-6" disabled={props?.showGlobalImpactWarning} />
                    )}
                  />

                  <AddressFieldGroup form={form} fields="address" />

                  <form.AppField name="contact" children={field => <field.ContactGroup hidePreferredCommunication />} />
                </Flex>
              </ProCard>
            ),
          },
          {
            name: 'misc',
            title: t`Sonstiges`,
            children: (
              <ProCard>
                <Flex vertical gap="small">
                  <form.AppField
                    name="comment"
                    children={field => <field.TextArea label={t`Bemerkungen`} className="grow" />}
                  />

                  <form.AppField
                    name="remarks"
                    children={field => <field.TextArea label={t`Besonderheiten`} className="grow" />}
                  />

                  <form.AppField name="serviceArea" children={field => <field.TextArea label="Versorgungsbereich" />} />
                </Flex>
              </ProCard>
            ),
          },
        ]}
      />
    </EntityDrawerShell>
  )
})
